import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { launchAgentWithWidget, startChatSession, useChatStore } from '@/features/chats';
import {
  getSmartStartCards,
  getSpringboardTabByIdMerged,
  SmartStartGridV4,
  ToolGridV4,
  useSmartStartCards,
  useSpringboardTabs,
  type SmartStartCardItem,
} from '@/features/springboard';
import { FEATURED_CARDS, FeaturedCard } from '@/features/tools-hub';
import { useContentStudioAllowed } from '@/shared/config/useContentStudioAllowed';
import {
  SectionPageHeader,
  SectionPageHeaderBackButton,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
} from '@/shared/ui/layout/SectionPageHeader/SectionPageHeader';
import { ListPageBody } from '@/shared/ui/layout/ListPageLayout/ListPageLayout';
import { ROUTES } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import { useTranslation } from 'react-i18next';
import { TOOL_BADGE_TINTS } from '@/shared/ui/ToolBadge';

// eslint-disable-next-line i18next/no-literal-string -- tool category id
const CREATE_TAB_ID = 'create';
const CREATE_FEATURED_CARD = FEATURED_CARDS.find((card) => card.id === 'content-studio');

export const CreatePage = () => {
  const { t } = useTranslation('tools');

  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Back returns to the surface the user came from (Tools, etc.), falling back
  // to Home when the page is reached directly (deep link, refresh, no origin).
  const backTo = (location.state as { from?: string } | null)?.from ?? ROUTES.HOME;

  const workspaceId = useCurrentWorkspaceId();
  const organizationId = getCurrentOrganizationId();
  const { isAllowed: isContentStudioAllowed, isLoading: isContentStudioLoading } =
    useContentStudioAllowed();
  const showContentStudioCard = !isContentStudioLoading && isContentStudioAllowed && !!workspaceId;
  const initializeChat = useChatStore((state) => state.initializeChat);

  const { tabs: springboardTabs } = useSpringboardTabs();

  const selectedTab = useMemo(
    () => getSpringboardTabByIdMerged(springboardTabs, CREATE_TAB_ID),
    [springboardTabs],
  );

  const { data: smartStartCards } = useQuery({
    queryKey: ['smart-start-cards', CREATE_TAB_ID],
    queryFn: () => getSmartStartCards(CREATE_TAB_ID),
    enabled: !!workspaceId,
  });

  const smartStartAgentIds = useMemo(
    () => (smartStartCards ?? []).map((card) => card.input.agentId),
    [smartStartCards],
  );

  const { isAgentEnabled } = useAgentVisibility(smartStartAgentIds, {
    workspaceId,
    organizationId,
  });

  const { canOpenTool } = usePermissions();

  const smartCardsResponse = useSmartStartCards({
    tabId: CREATE_TAB_ID,
    enabled: !!workspaceId,
    isAgentEnabled,
    canOpenAgent: canOpenTool,
  });

  const launchAgent = useCallback(
    (agentId: string) => {
      launchAgentWithWidget(navigate, agentId, { backTo: location.pathname });
    },
    [navigate, location.pathname],
  );

  const startSession = useCallback(
    async (item: SmartStartCardItem) => {
      await startChatSession(initializeChat, navigate, {
        agentId: item.input.agentId,
        prompt: item.input.prompt || '',
        inputs: item.input.options,
        visibilityScope: { workspaceId, organizationId },
        backTo: location.pathname,
      });
    },
    [initializeChat, navigate, workspaceId, organizationId, location.pathname],
  );

  if (!selectedTab) {
    return null;
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div ref={containerRef} className="flex flex-col w-full gap-4 p-4">
        <StaggerItem index={0}>
          <SectionPageHeader>
            <SectionPageHeaderBackButton
              onClick={() => void navigate(backTo)}
              aria-label={t('toolsHub.createPage.back')}
            />
            <SectionPageHeaderIcon surfaceClassName={TOOL_BADGE_TINTS[selectedTab.id]}>
              <Sparkles className="size-6" />
            </SectionPageHeaderIcon>
            <SectionPageHeaderContent>
              <SectionPageHeaderTitle>{selectedTab.label}</SectionPageHeaderTitle>
              <StaggerItem index={1} variant="fade">
                <SectionPageHeaderDescription>{selectedTab.subtitle}</SectionPageHeaderDescription>
              </StaggerItem>
            </SectionPageHeaderContent>
          </SectionPageHeader>
        </StaggerItem>

        {CREATE_FEATURED_CARD && showContentStudioCard && (
          <StaggerItem index={2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FeaturedCard
                id={CREATE_FEATURED_CARD.id}
                title={CREATE_FEATURED_CARD.title}
                description={CREATE_FEATURED_CARD.description}
                poster={CREATE_FEATURED_CARD.poster}
                video={CREATE_FEATURED_CARD.video}
                category={CREATE_FEATURED_CARD.category}
                badgeIcon={CREATE_FEATURED_CARD.badgeIcon}
                linkTo={CREATE_FEATURED_CARD.linkTo}
                index={0}
              />
            </div>
          </StaggerItem>
        )}

        <StaggerItem index={3}>
          <ListPageBody className="gap-6 p-6">
            <h3 className="text-lg font-medium">{t('toolsHub.createPage.allTools')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <ToolGridV4
                selectedTab={selectedTab}
                showFlowDigest={false}
                onToolClick={launchAgent}
                className="contents"
              />
            </div>

            {!!smartCardsResponse.cards.length && (
              <>
                <h3 className="text-lg font-medium">{t('toolsHub.createPage.smartStarts')}</h3>
                <SmartStartGridV4
                  smartCardsResponse={smartCardsResponse}
                  tabId={selectedTab.id}
                  onCardClick={(item) => void startSession(item)}
                />
              </>
            )}
          </ListPageBody>
        </StaggerItem>

        {/* MFINS-2828: GuidedLearningWorkflow temporarily hidden */}
        {/* <GuidedLearningWorkflow /> */}
      </div>
    </div>
  );
};
